/**
 * Distinguished scientists, academicians and public figures who have visited
 * the institute, from the institute's own page:
 * https://www.bietdvg.edu/major_events
 *
 * That page is nothing but this list — twenty guests and forty-seven
 * photographs, served there as unresized DSLR originals totalling 118 MB. The
 * same forty-seven are ingested to sites/biet/visits/ through the image
 * pipeline, so the site serves them at a sane size.
 *
 * The source prints each honour into the name ("Padmashri Dr. Girish
 * Bharadwaj"); it is a field here so the page can group and filter by it.
 * Spellings are corrected where the source plainly slipped — "M D PAllavi",
 * "Padmbhushan", "Chaterjee", "Banglore", "Vikaram Sarabhai", "Sengeetha",
 * "Thiruvanthapuram" — and nothing else is reworded.
 *
 * Only two entries carry a date. The rest are undated on the source and are
 * left that way rather than guessing a year from the photograph.
 */
import type { Media } from "@zivin-labs/site-content";

/** Highest to lowest, which is also the order the page groups them in. */
export type Honour = "Bharat Ratna" | "Padma Vibhushan" | "Padma Bhushan" | "Padma Shri";

export const HONOURS: Honour[] = ["Bharat Ratna", "Padma Vibhushan", "Padma Bhushan", "Padma Shri"];

export interface Visit {
  slug: string;
  /** Without the honour prefix the source bakes into it. */
  name: string;
  honour?: Honour;
  /** The post or body the source names. */
  affiliation: string;
  /** The source's own description, where it gives one beyond the post. */
  blurb?: string;
  /** Only where the source states one. */
  date?: string;
  /** Every photograph the source publishes for this visit, in its order. */
  photos: Media[];
}

/**
 * Photo dimensions are the ingested ones, not the originals — the pipeline caps
 * the long edge at 2560, so most of these came down from full-frame DSLR files.
 */
function shots(slug: string, name: string, dims: Array<[number, number]>): Media[] {
  return dims.map(([width, height], i) => ({
    url: `sites/biet/visits/${slug}-${String(i + 1).padStart(2, "0")}.jpg`,
    alt: `${name} at BIET`,
    width,
    height,
  }));
}

export const VISITS: Visit[] = [
  {
    slug: "apj-abdul-kalam",
    name: "Dr. A P J Abdul Kalam",
    honour: "Bharat Ratna",
    affiliation: "11th President of India, 2002 to 2007",
    blurb: "An internationally acclaimed Indian aerospace scientist.",
    photos: shots("apj-abdul-kalam", "Dr. A P J Abdul Kalam", [
      [2560, 2467],
      [2560, 1627],
      [2560, 1787],
    ]),
  },
  {
    slug: "c-n-r-rao",
    name: "Dr. C N R Rao",
    honour: "Bharat Ratna",
    affiliation: "Jawaharlal Nehru Centre for Advanced Scientific Research, Bangalore",
    blurb:
      "Served as Linus Pauling Research Professor, across the Centre's CSIR Centre of Excellence in Chemistry, New Chemistry Unit and International Centre for Materials Science. Chaired the Scientific Advisory Council to the Prime Minister for two terms — 1985 to 1989, and 2005 to 2014.",
    photos: shots("c-n-r-rao", "Dr. C N R Rao", [
      [2560, 1718],
      [2560, 1718],
    ]),
  },
  {
    slug: "g-madhavan-nair",
    name: "Dr. G. Madhavan Nair",
    honour: "Padma Vibhushan",
    affiliation: "Former Chairman, Indian Space Research Organisation, Bangalore",
    blurb: "Former Director, Vikram Sarabhai Space Centre, Trivandrum.",
    photos: shots("g-madhavan-nair", "Dr. G. Madhavan Nair", [[1280, 670]]),
  },
  {
    slug: "r-chidambaram",
    name: "Dr. R. Chidambaram",
    honour: "Padma Vibhushan",
    affiliation: "Former Principal Scientific Adviser to the Government of India",
    photos: shots("r-chidambaram", "Dr. R. Chidambaram", [[2560, 1718]]),
  },
  {
    slug: "vasudev-k-atre",
    name: "Dr. Vasudev K. Atre",
    honour: "Padma Vibhushan",
    affiliation: "Former Head, Defence Research and Development Organisation",
    blurb: "Former Scientific Adviser to the Defence Minister (Raksha Mantri).",
    photos: shots("vasudev-k-atre", "Dr. Vasudev K. Atre", [[1800, 1200]]),
  },
  {
    slug: "v-k-saraswat",
    name: "Dr. V K Saraswat",
    honour: "Padma Bhushan",
    affiliation: "Former Director General, Defence Research and Development Organisation",
    blurb:
      "Chief Scientific Adviser to the Indian Minister of Defence, and Chancellor of Jawaharlal Nehru University. A key scientist in the development of the Prithvi missile and its induction into the Indian armed forces.",
    photos: shots("v-k-saraswat", "Dr. V K Saraswat", [[2187, 1797]]),
  },
  {
    slug: "m-mahadevappa",
    name: "Dr. M. Mahadevappa",
    honour: "Padma Bhushan",
    affiliation:
      "Chairman, Agricultural Scientists Recruitment Board, Indian Council of Agricultural Research",
    blurb: "An internationally renowned rice breeder.",
    photos: shots("m-mahadevappa", "Dr. M. Mahadevappa", [[2560, 1718]]),
  },
  {
    slug: "g-padmanabhan",
    name: "Dr. G. Padmanabhan",
    honour: "Padma Bhushan",
    affiliation: "Former Director, Indian Institute of Science, Bangalore",
    blurb:
      "An Indian biochemist and biotechnologist. President, Indian National Science Academy, Bangalore.",
    photos: shots("g-padmanabhan", "Dr. G. Padmanabhan", [
      [2560, 1718],
      [2560, 1718],
      [2560, 1718],
    ]),
  },
  {
    slug: "b-n-suresh",
    name: "Dr. B.N. Suresh",
    honour: "Padma Bhushan",
    affiliation: "Chancellor, Indian Institute of Space Science and Technology, Thiruvananthapuram",
    blurb:
      "An Indian aerospace scientist, awarded the Padma Bhushan by the President of India in 2013 for meritorious contributions in science and technology.",
    photos: shots("b-n-suresh", "Dr. B.N. Suresh", [[2560, 1718]]),
  },
  {
    slug: "anil-prakash-joshi",
    name: "Dr. Anil Prakash Joshi",
    honour: "Padma Bhushan",
    affiliation:
      "Founder Chairman, Himalayan Environmental Studies and Conservation Organization, Dehradun",
    photos: shots("anil-prakash-joshi", "Dr. Anil Prakash Joshi", [
      [2560, 1696],
      [2560, 1696],
    ]),
  },
  {
    slug: "a-s-kiran-kumar",
    name: "A.S. Kiran Kumar",
    honour: "Padma Shri",
    affiliation: "Former Chairman, Indian Space Research Organisation, Bangalore",
    blurb:
      "Former Director, Space Applications Centre, Ahmedabad. Credited with the development of key scientific instruments aboard the Chandrayaan-1 and Mangalyaan spacecraft.",
    photos: shots("a-s-kiran-kumar", "A.S. Kiran Kumar", [
      [2560, 1696],
      [2560, 1718],
    ]),
  },
  {
    slug: "deepankar-chatterjee",
    name: "Dr. Deepankar Chatterjee",
    honour: "Padma Shri",
    affiliation:
      "Molecular biologist, Molecular Biophysics Unit, Indian Institute of Science, Bangalore",
    photos: shots("deepankar-chatterjee", "Dr. Deepankar Chatterjee", [
      [2560, 1718],
      [2560, 1718],
    ]),
  },
  {
    slug: "girish-bharadwaj",
    name: "Dr. Girish Bharadwaj",
    honour: "Padma Shri",
    affiliation: "Social worker",
    blurb:
      "Often referred to as Sethu Bandhu, and as the Bridgeman of India, for building around 127 bridges in remote villages across the country.",
    photos: shots("girish-bharadwaj", "Dr. Girish Bharadwaj", [
      [2560, 1718],
      [2560, 1718],
    ]),
  },
  {
    slug: "nileema-mishra",
    name: "Ms. Nileema Mishra",
    honour: "Padma Shri",
    affiliation: "Social worker, Maharashtra",
    blurb:
      "Received the Ramon Magsaysay Award for Emergent Leadership in 2011, and the Padma Shri in 2013 for social work. Visited for the International Women's Day celebration.",
    photos: shots("nileema-mishra", "Ms. Nileema Mishra", [
      [2560, 1707],
      [2560, 1707],
      [2560, 1707],
    ]),
  },
  {
    slug: "siddaramaiah",
    name: "Sri Siddaramaiah",
    affiliation: "Former Chief Minister of Karnataka",
    photos: shots("siddaramaiah", "Sri Siddaramaiah", [
      [1379, 926],
      [1379, 926],
      [1379, 926],
      [1379, 926],
    ]),
  },
  {
    slug: "s-sadagopan",
    name: "Dr. S. Sadagopan",
    affiliation: "Director, IIIT Bangalore",
    blurb: "Visited with his team.",
    date: "30 August 2023",
    photos: shots("s-sadagopan", "Dr. S. Sadagopan", [
      [1600, 1066],
      [1600, 1066],
      [1600, 1066],
      [1600, 1066],
      [1600, 1066],
    ]),
  },
  {
    slug: "isro-team",
    name: "ISRO team",
    affiliation: "U R Rao Satellite Centre, ISRO, Bengaluru",
    blurb:
      "Sri Ramanagouda V Nadagouda, Deputy Director, Integration and Checkout Area, and Dr. Raghavendra B Kulkarni, Chairperson, WSW-2023, were the chief guests.",
    photos: shots("isro-team", "The ISRO team", [
      [2560, 1707],
      [2560, 1707],
      [2560, 1707],
      [2560, 1707],
      [2560, 1707],
      [2560, 1707],
    ]),
  },
  {
    slug: "chidananda-gowda",
    name: "Dr. K. Chidananda Gowda",
    affiliation: "Former Vice-Chancellor, Kuvempu University",
    blurb:
      "Awarded the Kannada Sahitya Akademi award in 1986 for his book Putaanigala Vignyana Padyagalu, the M. Visveswaraya Technical Award in 1995 for his contributions to engineering, and the Rajyotsava award in 2019.",
    photos: shots("chidananda-gowda", "Dr. K. Chidananda Gowda", [
      [2560, 1707],
      [2560, 1707],
      [2560, 1707],
    ]),
  },
  {
    slug: "m-d-pallavi",
    name: "M. D. Pallavi",
    affiliation: "Kannada Sugama Sangeetha and playback singer, and television actor",
    blurb:
      "Trained in Hindustani classical music, with a degree in Hindustani music from the University of Benares, and trained in Sugama Sangeetha under Mysore Ananthaswamy. Visited for the Shivarathri programme.",
    photos: shots("m-d-pallavi", "M. D. Pallavi", [
      [2560, 1707],
      [2560, 1707],
      [2560, 1707],
    ]),
  },
  {
    slug: "gayathri-patil",
    name: "Dr. Gayathri Patil",
    affiliation: "Gynaecologist and obstetrician, SSIMS, Davanagere",
    blurb:
      "Visited for the Cancer Awareness and Screening Programme organised by BIET with the SS Care Trust.",
    date: "7 June 2024",
    photos: shots("gayathri-patil", "Dr. Gayathri Patil", [[868, 1156]]),
  },
];

export const VISIT_COUNT = VISITS.length;
export const VISIT_PHOTO_COUNT = VISITS.reduce((n, v) => n + v.photos.length, 0);

/** Honour tiers in rank order, skipping any that hold nobody. */
export function visitsByHonour(): Array<{ honour: Honour | null; label: string; visits: Visit[] }> {
  const tiers: Array<{ honour: Honour | null; label: string; visits: Visit[] }> = HONOURS.map((h) => ({
    honour: h,
    label: h as string,
    visits: VISITS.filter((v) => v.honour === h),
  }));
  const rest = VISITS.filter((v) => !v.honour);
  if (rest.length) tiers.push({ honour: null, label: "Other distinguished guests", visits: rest });
  return tiers.filter((t) => t.visits.length > 0);
}

export function getVisit(slug: string): Visit | undefined {
  return VISITS.find((v) => v.slug === slug);
}
